import React from 'react';
import { logger } from '@/utils/logger';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useCartStore } from '@/store/cartStore';
import type { Database } from '@/lib/database.types';
import { ImageWithFallback } from '@/components/ImageWithFallback';

type Product = Database['public']['Tables']['products']['Row'] & {
  producers: Database['public']['Tables']['producers']['Row'];
  categories: Database['public']['Tables']['categories']['Row'];
  price_per_kg?: number | null;
};

type Category = Database['public']['Tables']['categories']['Row'];
type Producer = Database['public']['Tables']['producers']['Row'];
type SortOption = 'name_asc' | 'name_desc' | 'price_asc' | 'price_desc';

export function Products() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = React.useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = React.useState<Product[]>([]);
  const [categories, setCategories] = React.useState<Category[]>([]);
  const [activeCategories, setActiveCategories] = React.useState<Category[]>([]);
  const [producers, setProducers] = React.useState<Producer[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [categoriesLoading, setCategoriesLoading] = React.useState(true);
  const [producersLoading, setProducersLoading] = React.useState(true);
  const [mobileFiltersOpen, setMobileFiltersOpen] = React.useState(false);
  const addItem = useCartStore((state) => state.addItem);
  
  // Get filters from URL params
  const categoryId = searchParams.get('category');
  const producerId = searchParams.get('producer');
  const inStock = searchParams.get('inStock');
  const searchQuery = searchParams.get('search');
  const sortBy = searchParams.get('sort') as SortOption || 'name_asc';

  // Fetch categories
  React.useEffect(() => {
    async function fetchCategories() {
      try {
        const { data, error } = await supabase
          .from('categories')
          .select('*')
          .order('name');

        if (error) throw error;
        setCategories(data || []);
      } catch (error) {
        logger.error('Error fetching categories:', error);
      } finally {
        setCategoriesLoading(false);
      }
    }

    fetchCategories();
  }, []);

  // Fetch producers
  React.useEffect(() => {
    async function fetchProducers() {
      try {
        const { data, error } = await supabase
          .from('producers')
          .select('*')
          .eq('is_approved', true)
          .order('company_name');

        if (error) throw error;
        setProducers(data || []);
      } catch (error) {
        logger.error('Error fetching producers:', error);
      } finally {
        setProducersLoading(false);
      }
    }

    fetchProducers();
  }, []);

  // Fetch products when category changes
  React.useEffect(() => {
    async function fetchProducts() {
      let query = supabase
        .from('products')
        .select(`
          *,
          producers (
            id,
            company_name,
            logo_url
          ),
          categories (
            name
          )
        `)
        .eq('is_published', true);

      // Don't filter at database level - we need all products to determine which categories have products
      // Category filtering will be done client-side
      if (producerId) {
        query = query.eq('producer_id', producerId);
      }

      const { data, error } = await query;

      if (error) {
        logger.error('Error fetching products:', error);
        return;
      }

      setProducts(data || []);
      setLoading(false);
    }

    fetchProducts();
  }, [producerId]);

  // Filter categories to only show those with products - based on ALL products, not filtered ones
  React.useEffect(() => {
    if (products.length > 0 && categories.length > 0) {
      // Create a Set of category IDs that have products (from all products, not filtered)
      const categoryIdsWithProducts = new Set(products.map(product => product.category_id));
      
      // Filter categories to only include those with products
      const nonEmptyCategories = categories.filter(category => 
        categoryIdsWithProducts.has(category.id)
      );
      
      setActiveCategories(nonEmptyCategories);
    } else {
      setActiveCategories([]);
    }
  }, [products, categories]);

  // Apply client-side filters and sorting
  React.useEffect(() => {
    let filtered = [...products];

    // Apply category filter client-side
    if (categoryId) {
      filtered = filtered.filter(product => product.category_id === categoryId);
    }

    // Apply in-stock filter
    if (inStock === 'true') {
      filtered = filtered.filter(product => product.stock_level > 0);
    }

    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(product => 
        product.name.toLowerCase().includes(query) || 
        product.description?.toLowerCase().includes(query) ||
        product.producers.company_name.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    switch (sortBy) {
      case 'name_asc':
        filtered.sort((a, b) => a.name.localeCompare(b.name, 'sv'));
        break;
      case 'name_desc':
        filtered.sort((a, b) => b.name.localeCompare(a.name, 'sv'));
        break;
      case 'price_asc':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price_desc':
        filtered.sort((a, b) => b.price - a.price);
        break;
    }

    setFilteredProducts(filtered);
  }, [products, categoryId, inStock, searchQuery, sortBy]);

  function handleFilterChange(filterName: string, value: string | null) {
    const newParams = new URLSearchParams(searchParams.toString());
    
    if (value === null) {
      newParams.delete(filterName);
    } else {
      newParams.set(filterName, value);
    }
    
    setSearchParams(newParams);
  }

  function resetFilters() {
    setSearchParams({});
  }

  if (loading || categoriesLoading || producersLoading) {
    return (
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mx-auto mb-8" />
            <div className="grid grid-cols-1 gap-y-10 sm:grid-cols-2 gap-x-6 lg:grid-cols-3 xl:grid-cols-4">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="space-y-4">
                  <div className="aspect-w-1 aspect-h-1 bg-gray-200 rounded-lg overflow-hidden" />
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error handling for API failures
  if (products.length === 0 && !loading && !categoriesLoading && !producersLoading) {
    return (
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="text-center" data-testid="error-message">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Ett fel uppstod vid hämtning av produkter</h3>
          <p className="text-gray-500">Försök igen senare eller kontakta support.</p>
        </div>
      </div>
    );
  }

  const selectedCategory = categories.find(cat => cat.id === categoryId);
  const selectedProducer = producers.find(prod => prod.id === producerId);
  const displayProducts = filteredProducts;

  return (
    <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
      {/* Mobile filter dialog */}
      {mobileFiltersOpen && (
        <div className="fixed inset-0 flex z-40 lg:hidden">
          <div className="fixed inset-0 bg-black bg-opacity-25" onClick={() => setMobileFiltersOpen(false)} />
          <div className="relative ml-auto flex h-full w-full max-w-xs flex-col overflow-y-auto bg-white py-4 pb-12 shadow-xl">
            <div className="flex items-center justify-between px-4">
              <h2 className="text-lg font-medium text-gray-900">Filter</h2>
              <button
                type="button"
                className="-mr-2 flex h-10 w-10 items-center justify-center rounded-md p-2 text-gray-400"
                onClick={() => setMobileFiltersOpen(false)}
              >
                <span className="sr-only">Stäng</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="px-4 pt-4 pb-6 space-y-6">
              {/* Search */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-2">Sök</h3>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <input
                    type="text"
                    name="mobile-search"
                    id="mobile-search"
                    value={searchQuery || ''}
                    onChange={(e) => handleFilterChange('search', e.target.value || null)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                    placeholder="Sök produkter..."
                    data-testid="mobile-search-input"
                  />
                </div>
              </div>

              {/* Sort options */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-2">Sortera</h3>
                <div className="mt-1">
                  <select
                    id="mobile-filter-sort"
                    name="mobile-filter-sort"
                    value={sortBy}
                    onChange={(e) => handleFilterChange('sort', e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                  >
                    <option value="name_asc">Namn: A till Ö</option>
                    <option value="name_desc">Namn: Ö till A</option>
                    <option value="price_asc">Pris: Lägst först</option>
                    <option value="price_desc">Pris: Högst först</option>
                  </select>
                </div>
              </div>

              {/* Categories */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-2">Kategorier</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => handleFilterChange('category', null)}
                    className={`block w-full text-left px-3 py-2 rounded ${
                      !categoryId
                        ? 'bg-green-50 text-green-700'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    Alla kategorier
                  </button>
                  {activeCategories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => handleFilterChange('category', category.id)}
                      className={`block w-full text-left px-3 py-2 rounded ${
                        categoryId === category.id
                          ? 'bg-green-50 text-green-700'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Producers */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-2">Producenter</h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  <button
                    onClick={() => handleFilterChange('producer', null)}
                    className={`block w-full text-left px-3 py-2 rounded ${
                      !producerId
                        ? 'bg-green-50 text-green-700'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    Alla producenter
                  </button>
                  {producers.map((producer) => (
                    <button
                      key={producer.id}
                      onClick={() => handleFilterChange('producer', producer.id)}
                      className={`block w-full text-left px-3 py-2 rounded ${
                        producerId === producer.id
                          ? 'bg-green-50 text-green-700'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {producer.company_name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Stock availability */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-2">Tillgänglighet</h3>
                <div className="mt-1">
                  <div className="flex items-center">
                    <input
                      id="mobile-in-stock"
                      name="mobile-in-stock"
                      type="checkbox"
                      checked={inStock === 'true'}
                      onChange={(e) => handleFilterChange('inStock', e.target.checked ? 'true' : null)}
                      className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <label htmlFor="mobile-in-stock" className="ml-2 text-sm text-gray-600">
                      Visa endast produkter i lager
                    </label>
                  </div>
                </div>
              </div>

              {/* Reset filters */}
              <div className="mt-4">
                <button
                  onClick={() => {
                    resetFilters();
                    setMobileFiltersOpen(false);
                  }}
                  className="w-full py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-md"
                >
                  Återställ filter
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="lg:grid lg:grid-cols-12 lg:gap-x-8">
        {/* Filters sidebar */}
        <div className="lg:col-span-3">
          <div className="sticky top-20 space-y-6">
            {/* Search */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-2">Sök</h2>
              <div className="mt-1 flex rounded-md shadow-sm">
                <input
                  type="search"
                  name="search"
                  id="search"
                  value={searchQuery || ''}
                  onChange={(e) => handleFilterChange('search', e.target.value || null)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm relative z-10"
                  placeholder="Sök produkter..."
                  data-testid="search-input"
                />
              </div>
            </div>

            {/* Sort options */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-2">Sortera</h2>
              <div className="mt-1">
                <select
                  id="sort"
                  name="sort"
                  value={sortBy}
                  onChange={(e) => handleFilterChange('sort', e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                  data-testid="sort-select"
                >
                  <option value="name_asc">Namn (A-Ö)</option>
                  <option value="name_desc">Namn: Ö till A</option>
                  <option value="price_asc">Pris (lägst först)</option>
                  <option value="price_desc">Pris (högst först)</option>
                </select>
              </div>
            </div>

            {/* Categories */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-2">Kategorier</h2>
              
              {/* Single select for tests and functionality */}
              <div className="mb-2">
                <select
                  value={categoryId || ''}
                  onChange={(e) => handleFilterChange('category', e.target.value || null)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                  data-testid="category-filter"
                  id="category-filter-main"
                  name="category-filter"
                >
                  <option value="">Alla kategorier</option>
                  {activeCategories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Producers */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-2">Producenter</h2>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                <button
                  onClick={() => handleFilterChange('producer', null)}
                  className={`block w-full text-left px-3 py-2 rounded ${
                    !producerId
                      ? 'bg-green-50 text-green-700'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Alla producenter
                </button>
                {producers.map((producer) => (
                  <button
                    key={producer.id}
                    onClick={() => handleFilterChange('producer', producer.id)}
                    className={`block w-full text-left px-3 py-2 rounded ${
                      producerId === producer.id
                        ? 'bg-green-50 text-green-700'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {producer.company_name}
                  </button>
                ))}
              </div>
            </div>

            {/* Stock availability */}
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-2">Tillgänglighet</h2>
              <div className="mt-1">
                <div className="flex items-center">
                  <input
                    id="in-stock"
                    name="in-stock"
                    type="checkbox"
                    checked={inStock === 'true'}
                    onChange={(e) => handleFilterChange('inStock', e.target.checked ? 'true' : null)}
                    className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  <label htmlFor="in-stock" className="ml-2 text-sm text-gray-600">
                    Visa endast produkter i lager
                  </label>
                </div>
              </div>
            </div>

            {/* Reset filters */}
            <div className="mt-4">
              <button
                onClick={resetFilters}
                className="w-full py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-md"
              >
                Återställ filter
              </button>
            </div>
          </div>
        </div>

        {/* Product grid */}
        <div className="mt-6 lg:mt-0 lg:col-span-9">
          {/* Mobile filter button */}
          <div className="lg:hidden mb-4">
            <button
              type="button"
              onClick={() => setMobileFiltersOpen(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              <svg className="-ml-1 mr-2 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filter
            </button>
          </div>

          <div className="flex flex-wrap items-center justify-between mb-4">
            <h1 className="text-3xl font-extrabold text-gray-900 mb-2" data-testid="products-heading">
              {selectedCategory ? selectedCategory.name : 'Produkter'}
              {selectedProducer && ` från ${selectedProducer.company_name}`}
            </h1>
            
            {/* Mobile sort */}
            <div className="lg:hidden w-full sm:w-auto mt-3 sm:mt-0">
              <select
                id="mobile-sort"
                name="mobile-sort"
                value={sortBy}
                onChange={(e) => handleFilterChange('sort', e.target.value)}
                className="block w-full sm:w-auto rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
              >
                <option value="name_asc">Namn: A till Ö</option>
                <option value="name_desc">Namn: Ö till A</option>
                <option value="price_asc">Pris: Lägst först</option>
                <option value="price_desc">Pris: Högst först</option>
              </select>
            </div>
          </div>
          
          {/* Active filters */}
          <div className="flex flex-wrap gap-2 mb-6">
            {(categoryId || producerId || inStock === 'true' || searchQuery || sortBy !== 'name_asc') && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-gray-700">Aktiva filter:</span>
                {categoryId && (
                  <span className="inline-flex rounded-full items-center py-1 pl-3 pr-1 text-sm font-medium bg-green-100 text-green-700">
                    {categories.find(c => c.id === categoryId)?.name}
                    <button
                      type="button"
                      onClick={() => handleFilterChange('category', null)}
                      className="flex-shrink-0 ml-0.5 h-4 w-4 rounded-full inline-flex items-center justify-center text-green-400 hover:bg-green-200 hover:text-green-500 focus:outline-none focus:bg-green-500 focus:text-white"
                    >
                      <span className="sr-only">Ta bort filter för {categories.find(c => c.id === categoryId)?.name}</span>
                      <svg className="h-2 w-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                        <path strokeLinecap="round" strokeWidth="1.5" d="M1 1l6 6m0-6L1 7" />
                      </svg>
                    </button>
                  </span>
                )}
                {producerId && (
                  <span className="inline-flex rounded-full items-center py-1 pl-3 pr-1 text-sm font-medium bg-green-100 text-green-700">
                    {producers.find(p => p.id === producerId)?.company_name}
                    <button
                      type="button"
                      onClick={() => handleFilterChange('producer', null)}
                      className="flex-shrink-0 ml-0.5 h-4 w-4 rounded-full inline-flex items-center justify-center text-green-400 hover:bg-green-200 hover:text-green-500 focus:outline-none focus:bg-green-500 focus:text-white"
                    >
                      <span className="sr-only">Ta bort filter för {producers.find(p => p.id === producerId)?.company_name}</span>
                      <svg className="h-2 w-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                        <path strokeLinecap="round" strokeWidth="1.5" d="M1 1l6 6m0-6L1 7" />
                      </svg>
                    </button>
                  </span>
                )}
                {inStock === 'true' && (
                  <span className="inline-flex rounded-full items-center py-1 pl-3 pr-1 text-sm font-medium bg-green-100 text-green-700">
                    Endast i lager
                    <button
                      type="button"
                      onClick={() => handleFilterChange('inStock', null)}
                      className="flex-shrink-0 ml-0.5 h-4 w-4 rounded-full inline-flex items-center justify-center text-green-400 hover:bg-green-200 hover:text-green-500 focus:outline-none focus:bg-green-500 focus:text-white"
                    >
                      <span className="sr-only">Ta bort lagerfilter</span>
                      <svg className="h-2 w-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                        <path strokeLinecap="round" strokeWidth="1.5" d="M1 1l6 6m0-6L1 7" />
                      </svg>
                    </button>
                  </span>
                )}
                {searchQuery && (
                  <span className="inline-flex rounded-full items-center py-1 pl-3 pr-1 text-sm font-medium bg-green-100 text-green-700">
                    Sök: {searchQuery}
                    <button
                      type="button"
                      onClick={() => handleFilterChange('search', null)}
                      className="flex-shrink-0 ml-0.5 h-4 w-4 rounded-full inline-flex items-center justify-center text-green-400 hover:bg-green-200 hover:text-green-500 focus:outline-none focus:bg-green-500 focus:text-white"
                    >
                      <span className="sr-only">Ta bort sökfilter</span>
                      <svg className="h-2 w-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                        <path strokeLinecap="round" strokeWidth="1.5" d="M1 1l6 6m0-6L1 7" />
                      </svg>
                    </button>
                  </span>
                )}
                {sortBy !== 'name_asc' && (
                  <span className="inline-flex rounded-full items-center py-1 pl-3 pr-1 text-sm font-medium bg-green-100 text-green-700">
                    {sortBy === 'name_desc' ? 'Namn: Ö till A' :
                     sortBy === 'price_asc' ? 'Pris: Lägst först' :
                     sortBy === 'price_desc' ? 'Pris: Högst först' : ''}
                    <button
                      type="button"
                      onClick={() => handleFilterChange('sort', 'name_asc')}
                      className="flex-shrink-0 ml-0.5 h-4 w-4 rounded-full inline-flex items-center justify-center text-green-400 hover:bg-green-200 hover:text-green-500 focus:outline-none focus:bg-green-500 focus:text-white"
                    >
                      <span className="sr-only">Återställ sortering</span>
                      <svg className="h-2 w-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                        <path strokeLinecap="round" strokeWidth="1.5" d="M1 1l6 6m0-6L1 7" />
                      </svg>
                    </button>
                  </span>
                )}
              </div>
            )}
          </div>
          
          {displayProducts.length === 0 ? (
            <div className="text-center text-gray-500 py-12">
              {searchQuery ? (
                <div data-testid="no-results-message">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Inga produkter hittades</h3>
                  <p>Försök med andra sökord eller ändra dina filter.</p>
                </div>
              ) : categoryId ? (
                <div data-testid="empty-category-message">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Inga produkter hittades i denna kategori</h3>
                  <p>Försök med en annan kategori eller ta bort filtren.</p>
                </div>
              ) : (
                <div data-testid="empty-products">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Inga produkter tillgängliga för tillfället</h3>
                  <p>Kom tillbaka senare eller kontakta oss för mer information.</p>
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-y-10 sm:grid-cols-2 gap-x-6 lg:grid-cols-3" data-testid="product-grid">
              {displayProducts.map((product) => (
                <div 
                  key={product.id} 
                  className="group relative flex flex-col"
                  data-testid="product-card"
                >
                  <Link to={`/products/${product.slug}`} className="relative">
                    <div className="w-full aspect-square bg-gray-200 rounded-md overflow-hidden">
                      <ImageWithFallback
                        src={product.image_urls?.[0] || ''}
                        alt={product.name}
                        className="w-full h-full object-contain hover:scale-105 transition-transform duration-200 bg-gray-50"
                        width={320}
                        height={320}
                        quality={75}
                      />
                    </div>
                    <div className="mt-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-sm font-medium text-gray-900" data-testid="product-name">
                            {product.name}
                          </h3>
                          <p className="mt-1 text-sm text-gray-500" data-testid="product-producer">
                            {product.producers.company_name}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900" data-testid="product-price">
                            {product.price.toLocaleString('sv-SE')} kr
                          </p>
                          {product.price_per_kg && (
                            <p className="text-xs text-gray-500 mt-1" data-testid="product-unit">
                              {product.price_per_kg.toLocaleString('sv-SE')} kr/kg
                            </p>
                          )}
                          <p className={`text-xs mt-1 stock-level availability ${product.stock_level > 0 ? 'text-green-600' : 'text-red-500'}`} data-testid="stock-level">
                            {product.stock_level > 0 
                              ? `I lager: ${product.stock_level} st` 
                              : 'Slut i lager'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Link>
                  <div className="mt-auto pt-3">
                    <button
                      onClick={async () => {
                        await addItem({
                          id: product.id,
                          name: product.name,
                          price: product.price,
                          producerId: product.producer_id
                        });
                      }}
                      disabled={product.stock_level <= 0}
                      className={`w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white
                        ${product.stock_level > 0
                          ? 'bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
                          : 'bg-gray-300 cursor-not-allowed'}`}
                      data-testid="add-to-cart"
                    >
                      {product.stock_level > 0 ? 'Lägg i kundvagn' : 'Slut i lager'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}